import React, { ReactNode } from 'react';

export interface GameRoomLayoutProps {
  /** Content for the left panel (typically PlayerPanel) */
  leftPanel: ReactNode;
  /** Content for the main area (typically CharacterCard) */
  mainContent: ReactNode;
  /** Optional header content */
  header?: ReactNode;
  /** Optional footer content */
  footer?: ReactNode;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * GameRoomLayout Component
 * 
 * A two-panel layout for the game room screen:
 * - Left panel: Fixed width (max 280px) for PlayerPanel
 * - Main content area: Flexible width for CharacterCard
 * - Responsive layout handling for smaller screens
 * 
 * Requirements: 1.1, 1.2, 1.4
 */
export const GameRoomLayout: React.FC<GameRoomLayoutProps> = ({
  leftPanel,
  mainContent,
  header,
  footer,
  className = '',
}) => {
  return (
    <div 
      className={`game-room-layout flex flex-col h-screen bg-gray-900 ${className}`}
      data-testid="game-room-layout"
    >
      {/* Optional Header */}
      {header && (
        <header 
          className="game-room-header flex-shrink-0 border-b border-gray-700 bg-gray-800/50"
          data-testid="game-room-header"
        >
          {header}
        </header>
      )}

      {/* Main Layout Container */}
      <div className="game-room-content flex flex-1 overflow-hidden">
        {/* Left Panel - Fixed width, max 280px (Requirement 1.4) */}
        <aside 
          className="game-room-left-panel flex-shrink-0 w-full max-w-[280px] overflow-y-auto border-r border-gray-700 bg-gray-900/50"
          data-testid="game-room-left-panel"
          role="complementary"
          aria-label="Players panel"
        >
          {leftPanel}
        </aside>

        {/* Main Content Area - Flexible width (Requirement 1.2) */}
        <main 
          className="game-room-main flex-1 overflow-y-auto p-6 bg-gray-900"
          data-testid="game-room-main"
          role="main"
          aria-label="Character details"
        >
          {mainContent}
        </main>
      </div>

      {/* Optional Footer */}
      {footer && (
        <footer 
          className="game-room-footer flex-shrink-0 border-t border-gray-700 bg-gray-800/50"
          data-testid="game-room-footer"
        >
          {footer}
        </footer>
      )}
    </div>
  );
};

export default GameRoomLayout;
